import { memo } from 'react';
import { useTheme } from 'styled-components';

import { useOtcCounterpartiesQueryData } from '@src/providers/Counterparties/useOtcCounterpartiesQueryData';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import type { DealOptionData } from '@src/types/OTC';
import { formatOtcDealDateTimeInTimezone } from '@src/utils/OTC/otcDealDisplayUtils';
import { Divider, HStack, InlineFormattedNumber, SideEnum, Stack, Text, VStack } from '@talos/kyoko';
import { Wrapper } from '../OMSCard/styles';
import { DealCardHeader } from './DealCardHeader';
import { useOtcDealSettlementTimezone } from './useOtcDealSettlementTimezone';

interface DealCardOptionProps {
  deal: DealOptionData;
}

export const DealCardOption = memo(function DealCardOption({ deal }: DealCardOptionProps) {
  const theme = useTheme();
  const { subAccountsByName } = useSubAccounts();
  const { otcCounterpartiesByName } = useOtcCounterpartiesQueryData();
  const settlementTimezone = useOtcDealSettlementTimezone(deal);
  const settleTime = deal.DealInfo?.SettleTime;

  const strikeLabel =
    deal.DealInfo.StrikeDelta != null && String(deal.DealInfo.StrikeDelta).trim() !== '' ? 'Strike Delta' : 'Strike';
  const strikeValue =
    deal.DealInfo.StrikeDelta != null && String(deal.DealInfo.StrikeDelta).trim() !== ''
      ? deal.DealInfo.StrikeDelta
      : deal.DealInfo.StrikePrice;

  return (
    <Wrapper>
      <Stack gap="spacing8" alignItems="stretch" p="spacing4">
        <DealCardHeader deal={deal} />

        <Divider color={theme.colors.gray['000']} />

        <VStack gap="spacing4" alignItems="stretch">
          <HStack justifyContent="space-between" alignItems="flex-start">
            <Text size="fontSizeSm" weight="fontWeightMedium" color={getSideColor(deal.DealInfo.Side)}>
              {deal.DealInfo.Side}
            </Text>
          </HStack>
          <Stack gap="spacing2">
            <HStack justifyContent="space-between">
              <Text size="fontSizeSmall">Notional</Text>
              <InlineFormattedNumber number={deal.DealInfo.NotionalAmount} currency={deal.DealInfo.NotionalAsset} />
            </HStack>
            {strikeValue != null && String(strikeValue).trim() !== '' ? (
              <HStack justifyContent="space-between">
                <Text size="fontSizeSmall">{strikeLabel}</Text>
                <Text size="fontSizeSmall" weight="fontWeightMedium">
                  {strikeValue}
                </Text>
              </HStack>
            ) : null}
            <HStack justifyContent="space-between">
              <Text size="fontSizeSmall">Settlement</Text>
              <Text size="fontSizeSmall" color="colorTextSecondary">
                {formatOtcDealDateTimeInTimezone(settleTime, settlementTimezone)}
              </Text>
            </HStack>
          </Stack>

          <HStack justifyContent="space-between">
            <Text size="fontSizeSmall" color="colorTextSecondary">
              Counterparty
            </Text>
            <Text size="fontSizeSmall" weight="fontWeightMedium">
              {deal.Counterparty
                ? (otcCounterpartiesByName?.get(deal.Counterparty)?.DisplayName ?? deal.Counterparty)
                : deal.Counterparty}
            </Text>
          </HStack>

          <HStack justifyContent="space-between">
            <Text size="fontSizeSmall" color="colorTextSecondary">
              Sub Account
            </Text>
            <Text size="fontSizeSmall" weight="fontWeightMedium">
              {deal.SubAccount
                ? (subAccountsByName.get(deal.SubAccount)?.DisplayName ?? deal.SubAccount)
                : deal.SubAccount}
            </Text>
          </HStack>
        </VStack>
      </Stack>
    </Wrapper>
  );
});

function getSideColor(side?: SideEnum) {
  return side === SideEnum.Buy ? 'colorTextPositive' : 'colorTextNegative';
}
